import Style from 'ol/style/Style.js';
import Stroke from 'ol/style/Stroke.js';
import Fill from 'ol/style/Fill.js';
import CircleStyle from 'ol/style/Circle.js';
import Text from 'ol/style/Text.js';

export const DRAW_STYLE_CONTROLS = Object.freeze({
    Point: Object.freeze({
        fill: true,
        outline: true,
        pointRadius: true,
    }),
    LineString: Object.freeze({
        line: true,
        lineStyle: true,
    }),
    Polygon: Object.freeze({
        fill: true,
        outline: true,
        outlineStyle: true,
    }),
    Circle: Object.freeze({
        fill: true,
        outline: true,
    }),
    Arrow: Object.freeze({
        fill: true,
        outline: true,
    }),
    Ellipse: Object.freeze({
        fill: true,
        outline: true,
    }),
    Freehand: Object.freeze({
        line: true,
        lineStyle: true,
    }),
    Text: Object.freeze({
        text: true,
    }),
});

const DEFAULT_CONFIGS = Object.freeze({
    Point: Object.freeze({
        fillColor: '#ff0000',
        fillOpacity: 1,
        outlineColor: '#ffffff',
        outlineWidth: 2,
        pointRadius: 15,
    }),
    LineString: Object.freeze({
        lineColor: '#0000ff',
        lineWidth: 2,
        lineStyle: 'Sólida',
    }),
    Polygon: Object.freeze({
        fillColor: '#00ff00',
        fillOpacity: 0.2,
        outlineColor: '#008000',
        outlineWidth: 2,
        outlineStyle: 'Sólida',
    }),
    Circle: Object.freeze({
        fillColor: '#ffff00',
        fillOpacity: 0.2,
        outlineColor: '#b38f00',
        outlineWidth: 2,
    }),
    Arrow: Object.freeze({
        fillColor: '#ff00ff',
        fillOpacity: 0.65,
        outlineColor: '#800080',
        outlineWidth: 2,
    }),
    Ellipse: Object.freeze({
        fillColor: '#00ffff',
        fillOpacity: 0.2,
        outlineColor: '#008b8b',
        outlineWidth: 2,
    }),
    Freehand: Object.freeze({
        lineColor: '#000000',
        lineWidth: 2,
        lineStyle: 'Sólida',
    }),
    Text: Object.freeze({
        textColor: '#000000',
        fontSize: 18,
    }),
});

export const createDrawStyleConfigs = () => Object.keys(DEFAULT_CONFIGS).reduce(
    (configs, type) => {
        configs[type] = { ...DEFAULT_CONFIGS[type] };
        return configs;
    },
    {}
);

export const normalizeDrawStyleConfig = (config = {}, drawingType) => {
    const defaults = DEFAULT_CONFIGS[drawingType] || {};
    const normalized = { ...defaults };

    if (drawingType === 'LineString' || drawingType === 'Freehand') {
        normalized.lineColor = config.lineColor || config.strokeColor || defaults.lineColor;
        normalized.lineWidth = Number(
            config.lineWidth ?? config.strokeWidth ?? defaults.lineWidth
        );
        normalized.lineStyle = config.lineStyle || defaults.lineStyle;
        return normalized;
    }

    if (drawingType === 'Text') {
        const legacyFontSize = 14 + (Number(config.strokeWidth ?? 2) - 1) * 3;
        normalized.textColor = config.textColor || config.strokeColor || defaults.textColor;
        normalized.fontSize = Number(config.fontSize ?? legacyFontSize);
        return normalized;
    }

    normalized.fillColor = config.fillColor
        || config.strokeColor
        || defaults.fillColor;
    normalized.fillOpacity = Number(config.fillOpacity ?? defaults.fillOpacity);
    normalized.outlineColor = config.outlineColor
        || config.strokeColor
        || defaults.outlineColor;
    normalized.outlineWidth = Number(
        config.outlineWidth ?? config.strokeWidth ?? defaults.outlineWidth
    );

    if (drawingType === 'Point') {
        normalized.pointRadius = Number(config.pointRadius ?? defaults.pointRadius);
    }
    if (drawingType === 'Polygon') {
        normalized.outlineStyle = config.outlineStyle
            || config.lineStyle
            || defaults.outlineStyle;
    }

    return normalized;
};

/**
 * Creates an OpenLayers style based on a tool-specific config object.
 * Legacy strokeColor/strokeWidth configs are accepted for imported drawings.
 * @param {Object} config
 * @param {string} [textValue] - The text content to display
 * @param {string} [drawingType] - The drawing tool that owns the style
 * @returns {Style[]}
 */
export const getStyleFromConfig = (config = {}, textValue, drawingType) => {
    const content = textValue || '';
    const hasText = content.length > 0;
    const isText = drawingType === 'Text' || hasText;

    const strokeColor = config.lineColor
        || config.outlineColor
        || config.strokeColor
        || '#000000';
    const strokeWidth = Number(
        config.lineWidth
        ?? config.outlineWidth
        ?? config.strokeWidth
        ?? 2
    );
    const lineStyle = config.lineStyle || config.outlineStyle || 'Sólida';
    const fillColor = config.fillColor || config.strokeColor || '#000000';
    const fillOpacity = Number(config.fillOpacity ?? 0.2);
    const pointRadius = Number(config.pointRadius ?? 10);
    const textColor = config.textColor || config.strokeColor || '#000000';
    const legacyFontSize = 14 + (Number(config.strokeWidth ?? 2) - 1) * 3;
    const fontSize = Number(config.fontSize ?? legacyFontSize);

    const fillRGBA = colorWithOpacity(fillColor, fillOpacity);
    const lineDash = getLineDash(lineStyle);
    const font = `bold ${fontSize}px sans-serif`;

    const mainStyle = new Style({
        stroke: new Stroke({
            color: strokeColor,
            width: strokeWidth,
            lineDash,
        }),
        fill: new Fill({
            color: fillRGBA,
        }),
        image: isText ? null : new CircleStyle({
            radius: pointRadius,
            fill: new Fill({
                color: fillRGBA,
            }),
            stroke: new Stroke({
                color: strokeColor,
                width: strokeWidth,
            }),
        }),
        text: new Text({
            text: content,
            font,
            textAlign: 'center',
            textBaseline: 'middle',
            offsetX: 0,
            offsetY: 0,
            fill: new Fill({
                color: textColor,
            }),
            stroke: new Stroke({
                color: '#ffffff',
                width: 3,
            }),
        }),
    });

    return [mainStyle];
};

const colorWithOpacity = (color, alpha) => {
    if (!color || typeof color !== 'string') return color;
    const hex = rgbaToHex(color);
    if (!hex || !hex.startsWith('#')) return color;

    const normalizedHex = hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
        : hex;
    const r = Number.parseInt(normalizedHex.slice(1, 3), 16);
    const g = Number.parseInt(normalizedHex.slice(3, 5), 16);
    const b = Number.parseInt(normalizedHex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbaToHex = (rgba) => {
    if (!rgba || typeof rgba !== 'string' || !rgba.startsWith('rgb')) return rgba;
    const parts = rgba.substring(rgba.indexOf('(') + 1, rgba.length - 1).split(',');
    const r = Number.parseInt(parts[0]).toString(16).padStart(2, '0');
    const g = Number.parseInt(parts[1]).toString(16).padStart(2, '0');
    const b = Number.parseInt(parts[2]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
};

const getLineDash = (lineStyle) => {
    if (lineStyle === 'Segmentada') return [10, 10];
    if (lineStyle === 'Punteada') return [2, 5];
    return null;
};

export const drawStyle = {
    data() {
        return {
            activeStyleArray: null,
        }
    },
    methods: {
        createDrawStyle: function() {
            // Style Reuse: Use a single instance for interaction and layer
            if (!this.activeStyleArray) {
                this.activeStyleArray = getStyleFromConfig(
                    this.drawStyleConfig,
                    '',
                    this.currentDrawingType
                );
            }
            return this.activeStyleArray;
        },
        updateDrawStyle: function (currentDrawingType) {
            this.activeStyleArray = getStyleFromConfig(
                this.drawStyleConfig,
                '',
                currentDrawingType
            );

            if (this.drawLayer) {
                this.drawLayer.setStyle(this.activeStyleArray);
                this.drawLayer.changed();
            }

            if (this.drawInteraction) {
                this.drawInteraction.getOverlay().setStyle(this.activeStyleArray);
            }
        },
    }
}
